import React from "react";
import { Line } from "react-chartjs-2";

import { CategoryScale, Chart, ChartOptions, Filler, LineElement, LinearScale, PointElement, ScriptableContext, ScriptableScaleContext } from "chart.js";

import { useTranslation } from "utils";

import { CHART_COLORS } from "consts";

import { Flex, Icon, Tooltip, Typography } from "components";

Chart.register(CategoryScale, Filler, LinearScale, LineElement, PointElement);

export type SocDevelopmentData = {
  points: { year: number; v: number }[];
  currentValue: number;
  currentYear: number;
};

type SocDevelopmentProps = {
  data: SocDevelopmentData;
};

const computeYAxis = (points: SocDevelopmentData["points"]) => {
  const values = points.map((p) => p.v);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const range = Math.abs(dataMax - dataMin) || 1;

  const rawStep = range / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const step = normalized <= 1 ? magnitude : normalized <= 2 ? 2 * magnitude : normalized <= 5 ? 5 * magnitude : 10 * magnitude;

  const tickStartIdx = Math.floor(dataMin / step);
  const tickEndIdx = Math.ceil(dataMax / step);
  const yMin = (tickStartIdx - 1) * step;
  const yMax = (tickEndIdx + 1) * step;
  const tickValues = Array.from({ length: tickEndIdx - tickStartIdx + 1 }, (_, i) => parseFloat(((tickStartIdx + i) * step).toFixed(10)));

  return { yMin, yMax, tickValues };
};

const SocLineChart = ({ points, currentYear }: { points: SocDevelopmentData["points"]; currentYear: number }) => {
  const labels = points.map((p) => String(p.year));
  const currentIdx = points.findIndex((p) => p.year === currentYear);
  const { yMin, yMax, tickValues } = computeYAxis(points);

  // Solid dataset: past + current year; null for future
  const solidValues = points.map((p, i) => (i <= currentIdx ? p.v : null));
  // Dashed dataset: null before current year, values from current onward
  const dashedValues = points.map((p, i) => (i >= currentIdx ? p.v : null));

  const data = {
    labels,
    datasets: [
      {
        data: solidValues,
        borderColor: CHART_COLORS["green-400"],
        borderWidth: 1.5,
        tension: 0,
        fill: true,
        clip: false as const,
        spanGaps: false,
        backgroundColor: (ctx: ScriptableContext<"line">): string | CanvasGradient => {
          const { ctx: canvasCtx, chartArea } = ctx.chart;
          if (!chartArea) return "transparent";
          const gradient = canvasCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(6, 92, 69, 0.25)");
          gradient.addColorStop(1, "rgba(6, 92, 69, 0)");
          return gradient;
        },
        pointBackgroundColor: (ctx: ScriptableContext<"line">) =>
          points[ctx.dataIndex]?.year === currentYear ? CHART_COLORS["green-400"] : CHART_COLORS.white,
        pointBorderColor: CHART_COLORS["green-400"],
        pointBorderWidth: (ctx: ScriptableContext<"line">) => (points[ctx.dataIndex]?.year === currentYear ? 0 : 1.5),
        pointRadius: (ctx: ScriptableContext<"line">) => (solidValues[ctx.dataIndex] !== null ? (points[ctx.dataIndex]?.year === currentYear ? 4 : 2.5) : 0),
        pointHoverRadius: 0,
      },
      {
        data: dashedValues,
        borderColor: CHART_COLORS["green-300"],
        borderWidth: 1.5,
        borderDash: [4, 4],
        tension: 0,
        fill: false,
        clip: false as const,
        spanGaps: false,
        backgroundColor: "transparent",
        pointBackgroundColor: CHART_COLORS.white,
        pointBorderColor: CHART_COLORS["green-400"],
        pointBorderWidth: (ctx: ScriptableContext<"line">) => (points[ctx.dataIndex]?.year === currentYear ? 0 : 1.5),
        pointRadius: (ctx: ScriptableContext<"line">) => (dashedValues[ctx.dataIndex] !== null && points[ctx.dataIndex]?.year !== currentYear ? 2.5 : 0),
        pointHoverRadius: 0,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    events: [],
    layout: { padding: { top: 8 } },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: { display: false },
    },
    scales: {
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: {
          color: (ctx: ScriptableScaleContext) => (points[ctx.index]?.year === currentYear ? CHART_COLORS.black : CHART_COLORS["grey-500"]),
          font: (ctx: ScriptableScaleContext) => ({
            family: "dazzed",
            size: 8,
            weight: points[ctx.index]?.year === currentYear ? 700 : 400,
          }),
        },
      },
      y: {
        min: yMin,
        max: yMax,
        border: {
          display: false,
          dash: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? [3, 3] : []),
        },
        grid: {
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? CHART_COLORS["green-300"] : "#e8e8e8"),
          lineWidth: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? 1 : 0.5),
        },
        ticks: {
          color: CHART_COLORS["grey-500"],
          font: { family: "dazzed", size: 7 },
          callback: (value) => {
            const v = Number(value);
            if (v === 0) return "0.00";
            return v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
          },
        },
        afterBuildTicks: (scale) => {
          scale.ticks = tickValues.map((value) => ({ value }));
        },
      },
    },
    animation: { duration: 300 },
  };

  return (
    <div className="soc-development-chart-wrapper">
      <Line data={data} options={options} />
    </div>
  );
};

export const SocDevelopment = React.memo(({ data }: SocDevelopmentProps) => {
  const { t } = useTranslation("fieldTimeline");
  const { points, currentValue, currentYear } = data;
  const sign = currentValue >= 0 ? "+" : "";

  return (
    <div className="soc-development">
      <Flex align="center" justify="space-between" className="soc-development-header">
        <Flex align="center" gap="0.375rem">
          <Typography variant="paragraph-m" bold className="soc-development-title">
            {t("impactPanel.socDevelopment.title", "SOC DEVELOPMENT")}
          </Typography>
          <Tooltip title={t("impactPanel.socDevelopment.tooltip", "Soil organic carbon development over time.")}>
            <span>
              <Icon iconName="tabler/info-circle" size="xs" className="soc-development-info-icon" />
            </span>
          </Tooltip>
        </Flex>
        <span className="soc-development-year-badge">{currentYear}</span>
      </Flex>

      <Flex align="baseline" gap="0.25rem" className="soc-development-value-row">
        <Typography variant="h3" className="soc-development-current-value">
          {sign}
          {currentValue.toFixed(2)}
        </Typography>
        <Typography variant="paragraph-s" className="soc-development-unit">
          t/ha
        </Typography>
      </Flex>

      <SocLineChart points={points} currentYear={currentYear} />
    </div>
  );
});
